import React, { useState, useEffect } from "react";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import "../theme-palette.css";
import { useOutletContext } from "react-router-dom";
import HeaderActionsPortal from "./HeaderActionsPortal";
import {
  Plus,
  Edit2,
  Trash2,
  Users,
  Shield,
  Layers,
  X,
  Save,
  Import,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { MODULES as AVAILABLE_MODULES, AUDIENCE } from "./Layout";
import { ACCESS_KEY_FOR, normaliseRole, isAdminRole } from "../roles";
import { useT } from "../i18n.jsx";

// Modules a module admin can currently be given rights over -- i.e. the ones with a settings
// document the rules hand to canManageModule(). Listing every module here would let an
// administrator name someone and grant nothing, which is worse than not offering the choice.
// Keep in step with the metadata/<module>_settings rules in firestore.rules.
const MODULE_ADMIN_CAPABLE = new Set([
  "parent_notice",
  "salary_audit",
  "attendance",
  "invigilation",
  // Its own admin list, not invigilation's: the SEN register records why a student holds an
  // accommodation, so the people who may read it are a deliberately smaller set than the people who
  // arrange the main exam.
  "sen_invigilation",
  "patrol_duty",
  "quiz_tutorial",
  "budget_control",
  "forms_and_application",
  "social_credit",
  "assessment",
  "substitution",
  "maintenance_report",
  "ole_management",
  // Duty assignment. Added when teach_duty / admin_duty stopped accepting writes from any member of
  // staff: without these two an administrator could narrow the rule and then have no way to name
  // the person who actually does the job, which would leave the duty coordinator locked out of the
  // module they run.
  "teaching_duty",
  "manpower_allocation",
  // Shared registers. These have no settings document of their own -- naming an admin here delegates
  // the records themselves, which were system-administrator-only to write.
  "calendar_admin",
  "timetable_profiles",
  "inventory_admin",
  "manage_it_assets",
  "campus_management",
]);

const AccessControl = () => {
  const { t } = useT();
  const { activeSchoolId, isMobile } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [personEmail, setPersonEmail] = useState("");
  const [personSearch, setPersonSearch] = useState("");

  // module_access | family_access | module_admins
  const [topTab, setTopTab] = useState("module_access");
  const [moduleAccess, setModuleAccess] = useState({});
  // { moduleId: [email, ...] }. One document for every module rather than a field invented per
  // module, so "who is elevated where" is one place to look and one read for the rules.
  const [moduleAdmins, setModuleAdmins] = useState({});
  const [savingModuleAdmins, setSavingModuleAdmins] = useState(false);
  const [adminPick, setAdminPick] = useState({});
  const [savingModuleAccess, setSavingModuleAccess] = useState(false);

  const [collapsedCategories, setCollapsedCategories] = useState({
    user: true,
    admin: true,
    school_admin: true,
    student_affairs: true,
    duties: true,
    system: true,
  });

  const toggleCategory = (categoryId) => {
    setCollapsedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newExceptionInput, setNewExceptionInput] = useState({});



  // Same grouping and same order as the sidebar, so a school configuring a module looks for it
  // where they see it. "family" only ever has anything in it on the Student & Parent tab -- the
  // staff tabs filter those modules out, and an empty group renders nothing.
  const CATEGORIES = [
    { id: "me", label: "MY WORK" },
    { id: "students", label: "STUDENTS" },
    { id: "assessments", label: "ASSESSMENTS" },
    { id: "administration", label: "ADMINISTRATION" },
    { id: "duties", label: "DUTY ARRANGEMENT" },
    { id: "booking", label: "BOOKING" },
    { id: "accounting", label: "ACCOUNTING" },
    { id: "resources", label: "RESOURCES" },
    { id: "system", label: "SYSTEM" },
    { id: "family", label: "STUDENT & PARENT" },
  ];


  // Resolves a stored exception value to something a school recognises. The value is an email;
  // what gets shown is the initial where one exists, which is how staff are named everywhere else.
  const exceptionLabel = (val) => {
    const st = staffList.find((x) => x.id === val);
    return st?.initial ? `${st.initial} (${val})` : val;
  };

  const ROLE_COLUMN_LABEL = (role) => t(`accessControl.roles.${role}`, {}, role);

  // One table, rendered twice: once for the staff roles over the staff modules, once for students
  // and guardians over the handful of modules that are actually theirs. Splitting the sections is
  // what stops a school being asked whether a parent should see Invigilation.
  const renderAccessTable = (roles, moduleFilter) => (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left",
                minWidth: "1000px",
              }}
            >
              <thead
                style={{
                  backgroundColor: "var(--color-bg-light)",
                  borderBottom: "1px solid var(--color-border)",
                }}
              >
                <tr>
                  <th
                    style={{
                      padding: "1rem",
                      color: "var(--color-text-muted)",
                      fontWeight: "600",
                      width: "15%",
                    }}
                  >
                    {t('accessControl.module')}
                  </th>
                  {roles.map((r) => (
                    <th
                      key={r}
                      style={{
                        padding: "1rem",
                        color: "var(--color-text-muted)",
                        fontWeight: "600",
                        width: `${64 / roles.length}%`,
                      }}
                    >
                      {ROLE_COLUMN_LABEL(r)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CATEGORIES.map((category) => {
                  const categoryModules = AVAILABLE_MODULES.filter(
                    (m) => m.category === category.id && moduleFilter(m),
                  );
                  if (categoryModules.length === 0) return null;
                  return (
                    <React.Fragment key={category.id}>
                      <tr
                        onClick={() => toggleCategory(category.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td
                          colSpan={roles.length + 1}
                          style={{
                            padding: "0.75rem 1rem",
                            backgroundColor: "var(--color-bg-subtle)",
                            fontWeight: "700",
                            color: "var(--color-text-main)",
                            fontSize: "0.85rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            userSelect: "none",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{category.label}</span>
                            {collapsedCategories[category.id] ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronUp size={16} />
                            )}
                          </div>
                        </td>
                      </tr>
                      {!collapsedCategories[category.id] &&
                        categoryModules.map((mod) => {
                          const config =
                            moduleAccess[mod.id] || getDefaultConfig(mod.id);

                          // One control per role. `some` remains the stored value -- it is what
                          // Layout's isModuleActive reads and what every school's saved config
                          // already holds -- while the label reads "Exception", which is what it
                          // actually means. Labels are not identifiers; renaming the stored token
                          // would be a migration for no gain.
                          const renderAccessCell = (role) => (
                            <td style={{ padding: "1rem", verticalAlign: "top" }}>
                              <select
                                className="input-field"
                                style={{ marginBottom: "0.5rem", width: "100%" }}
                                value={config[`${role}Access`] || "off"}
                                onChange={(e) =>
                                  handleModuleAccessChange(
                                    mod.id,
                                    role,
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="on">{t('accessControl.on')}</option>
                                <option value="off">{t('accessControl.off')}</option>
                                {/* Exception is staff-only. The list of who has one holds email
                                    addresses and stays in a staff-readable document, so a guardian
                                    could never be told they are on it -- offering the option to
                                    families would be a setting that silently means "off". */}
                                {(role === "teacher" || role === "staff") && (
                                  <option value="some">{t('accessControl.exception')}</option>
                                )}
                              </select>
                              {config[`${role}Access`] === "some" &&
                                (role === "teacher" || role === "staff"
                                  ? renderExceptionsPicker(role)
                                  : (
                                    <span style={{ fontSize: "0.75rem", color: "var(--color-danger)" }}>
                                      {t('accessControl.noFamilyExceptions')}
                                    </span>
                                  ))}
                            </td>
                          );

                          const renderExceptionsPicker = (role) => {
                            const isStaff =
                              role === "teacher" || role === "staff";
                            // The VALUE stored is the email -- the document id of a staff profile,
                            // and the only identifier a security rule could ever verify. The initial
                            // is the label, because that is what a school recognises. Lists saved
                            // before this hold initials and still work: Layout matches on either.
                            const availableStaff = staffList.map((st) => ({
                              value: st.id,
                              label: st.initial ? `${st.initial} — ${st.id}` : st.id,
                            }));
                            const selectedList = config[`${role}List`] || [];

                            return (
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "0.5rem",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  {isStaff ? (
                                    <select
                                      className="input-field"
                                      style={{ flex: 1 }}
                                      value={
                                        newExceptionInput[`${mod.id}_${role}`] ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        setNewExceptionInput((prev) => ({
                                          ...prev,
                                          [`${mod.id}_${role}`]: e.target.value,
                                        }))
                                      }
                                    >
                                      <option value="">-- Select --</option>
                                      {availableStaff
                                        .filter((st) => !selectedList.includes(st.value))
                                        .map((st) => (
                                          <option key={st.value} value={st.value}>
                                            {st.label}
                                          </option>
                                        ))}
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      className="input-field"
                                      style={{ flex: 1 }}
                                      placeholder={
                                        role === "parent"
                                          ? "Guardian email"
                                          : "Student email"
                                      }
                                      value={
                                        newExceptionInput[`${mod.id}_${role}`] ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        setNewExceptionInput((prev) => ({
                                          ...prev,
                                          [`${mod.id}_${role}`]: e.target.value,
                                        }))
                                      }
                                    />
                                  )}
                                  <button
                                    className="btn-primary"
                                    style={{
                                      padding: "0.5rem 1rem",
                                      width: "auto",
                                      whiteSpace: "nowrap",
                                    }}
                                    onClick={() =>
                                      handleAddException(mod.id, role)
                                    }
                                  >
                                    Add
                                  </button>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "0.5rem",
                                  }}
                                >
                                  {selectedList.map((val) => (
                                    <span
                                      key={val}
                                      style={{
                                        backgroundColor: "var(--color-bg-app)",
                                        padding: "0.25rem 0.75rem",
                                        borderRadius: "1rem",
                                        fontSize: "0.875rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        border: "1px solid var(--color-border)",
                                      }}
                                    >
                                      {exceptionLabel(val)}
                                      <button
                                        onClick={() =>
                                          handleRemoveException(mod.id, role, val)
                                        }
                                        style={{
                                          background: "none",
                                          border: "none",
                                          cursor: "pointer",
                                          color: "var(--color-text-muted)",
                                          padding: "0",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          };

                          return (
                            <tr
                              key={mod.id}
                              style={{
                                borderBottom: "1px solid var(--color-border)",
                              }}
                            >
                              <td
                                style={{
                                  padding: "1rem",
                                  fontWeight: "600",
                                  color: "var(--color-text-main)",
                                  verticalAlign: "top",
                                  paddingTop: "1.5rem",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {mod.icon && (
                                    <mod.icon
                                      size={16}
                                      style={{
                                        marginRight: "0.5rem",
                                        color: "var(--color-primary)",
                                      }}
                                    />
                                  )}
                                  {mod.name}
                                </div>
                              </td>
                              {roles.map((r) => (
                                <React.Fragment key={r}>{renderAccessCell(r)}</React.Fragment>
                              ))}
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
  );

  const renderSection = (title, blurb, roles, moduleFilter) => (
    <div style={{ marginBottom: "2rem" }}>
      <h3 style={{ fontSize: "1rem", fontWeight: "700", margin: "0 0 0.25rem" }}>{title}</h3>
      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0 0 0.75rem" }}>{blurb}</p>
      {renderAccessTable(roles, moduleFilter)}
    </div>
  );

  // Every module declares its own default in MODULES now, so there is nothing to infer from which
  // sidebar section it happens to sit in. That inference used to live here and in Layout, which
  // meant regrouping the sidebar quietly changed who could open what.

  const getDefaultConfig = (moduleId) => {
    const moduleInfo = AVAILABLE_MODULES.find((m) => m.id === moduleId);
    return {
      // Closed is the fallback for an id this build no longer knows: a module a school still has
      // saved should not open itself to everyone on its way out.
      teacherAccess: "off",
      staffAccess: "off",
      studentAccess: "off",
      // Parents are outside adults: nothing is open to them unless a school says so.
      parentAccess: "off",
      ...(moduleInfo?.defaultAccess || {}),
      teacherList: [],
      staffList: [],
      studentList: [],
      parentList: [],
    };
  };

  // --- By person -------------------------------------------------------------------------------
  // The same settings as the Staff Modules tab, read down a column instead of across a row.
  //
  // Fifteen of the modules belong to a post rather than to everyone who teaches, and the only way
  // to express that is `some` plus a list of addresses. Maintained module by module, that is
  // fifteen lists to keep in step, and the cheap way out is to set the lot to `on` -- at which
  // point every teacher opens the portal to thirty entries and the defaults agreed above have
  // bought nothing. This tab exists so granting the timetable master their two modules is one
  // screen rather than a hunt through fifteen rows.
  //
  // It writes into `moduleAccess`, the same state the other tab edits and the same Save button
  // commits. There is no second copy of this data and no second way to save it.
  const personRoleKey = (person) => ACCESS_KEY_FOR[normaliseRole(person?.role)];

  // What this person's access to one module actually is, and whether this screen can change it.
  const personStateFor = (moduleId, roleKey, email) => {
    const cfg = moduleAccess[moduleId] || getDefaultConfig(moduleId);
    const level = cfg[`${roleKey}Access`];
    if (level === "on") return { on: true, locked: true, reason: "On for everyone in this role" };
    if (level === "some") return { on: (cfg[`${roleKey}List`] || []).includes(email), locked: false };
    return { on: false, locked: false };
  };

  const togglePersonModule = (moduleId, roleKey, email) => {
    setModuleAccess((prev) => {
      const next = { ...prev };
      const cfg = { ...(next[moduleId] || getDefaultConfig(moduleId)) };
      const list = [...(cfg[`${roleKey}List`] || [])];
      const at = list.indexOf(email);
      if (at >= 0) {
        list.splice(at, 1);
        // An empty `some` behaves exactly like `off` but reads like a list somebody forgot to
        // fill in. Collapse it, so the Staff Modules tab tells the same story as this one.
        cfg[`${roleKey}Access`] = list.length > 0 ? "some" : "off";
      } else {
        list.push(email);
        cfg[`${roleKey}Access`] = "some";
      }
      cfg[`${roleKey}List`] = list;
      next[moduleId] = cfg;
      return next;
    });
  };

  const renderByPerson = () => {
    const q = personSearch.trim().toLowerCase();
    const people = staffList
      .filter((st) => !q || `${st.initial || ""} ${st.firstName || ""} ${st.lastName || ""} ${st.id}`.toLowerCase().includes(q))
      .sort((a, b) => (a.initial || a.id).localeCompare(b.initial || b.id));

    const person = staffList.find((st) => st.id === personEmail);
    const role = normaliseRole(person?.role);
    const roleKey = personRoleKey(person);
    const staffModules = AVAILABLE_MODULES.filter((m) => m.audience !== AUDIENCE.FAMILY);
    const granted = person && roleKey
      ? staffModules.filter((m) => personStateFor(m.id, roleKey, person.id).on).length
      : 0;

    return (
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 260px", minWidth: 0, border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
          <div style={{ padding: "0.5rem" }}>
            <input className="input-field" value={personSearch} onChange={(e) => setPersonSearch(e.target.value)}
                   placeholder={t('accessControl.searchStaff')} style={{ margin: 0 }} />
          </div>
          <div style={{ maxHeight: "60vh", overflowY: "auto", borderTop: "1px solid var(--color-border)" }}>
            {people.length === 0 && (
              <div style={{ padding: "0.75rem", fontSize: "0.83rem", color: "var(--color-text-muted)" }}>{t('accessControl.noMatches')}</div>
            )}
            {people.map((st) => (
              <button key={st.id} type="button" onClick={() => setPersonEmail(st.id)}
                      style={{
                        display: "block", width: "100%", textAlign: "left", border: "none", cursor: "pointer",
                        padding: "0.45rem 0.7rem", fontSize: "0.85rem",
                        backgroundColor: st.id === personEmail ? "var(--color-nav-active)" : "transparent",
                        fontWeight: st.id === personEmail ? "700" : "400",
                      }}>
                <div>{st.initial ? `${st.initial} — ` : ""}{`${st.lastName || ""} ${st.firstName || ""}`.trim() || st.id}</div>
                <div style={{ fontSize: "0.72rem", color: "var(--color-text-muted)", overflow: "hidden", textOverflow: "ellipsis" }}>{st.id}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: "2 1 420px", minWidth: 0 }}>
          {!person ? (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}>
              {t('accessControl.pickSomebody')}
            </div>
          ) : isAdminRole(role) ? (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}>
              {/* Said plainly rather than shown as a screen of ticked boxes that cannot be
                  unticked: an administrator bypasses module access entirely. */}
              {t('accessControl.isAdmin')}
            </div>
          ) : !roleKey ? (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}>
              {t('accessControl.noRole')}
            </div>
          ) : (
            <>
              <div style={{ marginBottom: "0.6rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                <strong style={{ color: "var(--color-text-main)" }}>{granted}</strong> modules — as {ROLE_COLUMN_LABEL[roleKey]}
              </div>
              <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
                {CATEGORIES.map((category) => {
                  const mods = staffModules.filter((m) => m.category === category.id);
                  if (mods.length === 0) return null;
                  return (
                    <React.Fragment key={category.id}>
                      <div style={{ padding: "0.4rem 0.7rem", backgroundColor: "var(--color-bg-app)", fontSize: "0.72rem", fontWeight: "700", color: "var(--color-text-muted)", borderBottom: "1px solid var(--color-border)" }}>
                        {category.label}
                      </div>
                      {mods.map((mod) => {
                        const st = personStateFor(mod.id, roleKey, person.id);
                        return (
                          <label key={mod.id}
                                 style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.4rem 0.7rem", borderBottom: "1px solid var(--color-border)", cursor: st.locked ? "default" : "pointer" }}>
                            <input type="checkbox" checked={st.on} disabled={st.locked}
                                   onChange={() => togglePersonModule(mod.id, roleKey, person.id)}
                                   style={{ width: "15px", height: "15px", accentColor: "var(--color-primary)", cursor: st.locked ? "default" : "pointer" }} />
                            <span style={{ fontSize: "0.85rem", flex: 1 }}>{mod.name}</span>
                            {/* A disabled tick with no explanation reads as a bug. This one is
                                disabled because the grant is not this person's to remove. */}
                            {st.locked && (
                              <span style={{ fontSize: "0.72rem", color: "var(--color-text-muted)" }}>{st.reason}</span>
                            )}
                          </label>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
              <p style={{ fontSize: "0.78rem", color: "var(--color-text-muted)", marginTop: "0.5rem" }}>
                {t('accessControl.exceptionHint')}
              </p>
            </>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!activeSchoolId) return;

    // Fetch Staff for mapping initials/emails
    const fetchStaff = async () => {
      const snap = await getDocs(
        collection(db, "schools", activeSchoolId, "staff_profiles"),
      );
      const staff = [];
      snap.forEach((d) => {
        staff.push({ id: d.id, ...d.data() });
      });
      setStaffList(staff);
    };
    fetchStaff();


    // Listen to Global Roles Configuration

    const unsubModuleAdmins = onSnapshot(
      doc(db, "schools", activeSchoolId, "metadata", "module_admins"),
      (snap) => setModuleAdmins(snap.exists() ? snap.data() : {}),
      (e) => console.error("Error loading module admins", e),
    );

    // The switches and the exception lists live in two documents so that students and guardians can
    // read the switches: the lists hold email addresses, and a document every member could read
    // would hand every family's address to every student. They are merged back into one shape here
    // so the table below is unchanged -- the split is a storage decision, not a UI one.
    let flagsDoc = {};
    let listsDoc = {};
    const applyMerged = () => {
      const merged = {};
      const ids = new Set([...Object.keys(flagsDoc), ...Object.keys(listsDoc)]);
      ids.forEach((id) => {
        merged[id] = { ...(flagsDoc[id] || {}), ...(listsDoc[id] || {}) };
      });
      setModuleAccess(merged);
    };

    const unsubModuleAccess = onSnapshot(
      doc(db, "schools", activeSchoolId, "metadata", "module_access"),
      (snap) => {
        flagsDoc = snap.exists() ? snap.data() : {};
        applyMerged();
        // This is the document the page is actually about, so it is what "loaded" means. It used to
        // hang off a listener on system_groups -- a collection nothing reads any more.
        setLoading(false);
      },
      (e) => {
        console.error("Error loading module access", e);
        setLoading(false);
      },
    );

    const unsubModuleExceptions = onSnapshot(
      doc(db, "schools", activeSchoolId, "metadata", "module_access_exceptions"),
      (snap) => { listsDoc = snap.exists() ? snap.data() : {}; applyMerged(); },
      (e) => console.error("Error loading module access exceptions", e),
    );

    return () => {
      unsubModuleAccess();
      unsubModuleExceptions();
      unsubModuleAdmins();
    };
  }, [activeSchoolId]);

  const handleModuleAccessChange = (moduleId, role, value) => {
    setModuleAccess((prev) => {
      const newAccess = { ...prev };
      if (!newAccess[moduleId]) {
        newAccess[moduleId] = getDefaultConfig(moduleId);
      }
      newAccess[moduleId][`${role}Access`] = value;
      return newAccess;
    });
  };

  const handleModuleAccessListChange = (moduleId, role, newValue) => {
    setModuleAccess((prev) => {
      const newAccess = { ...prev };
      if (!newAccess[moduleId]) {
        newAccess[moduleId] = getDefaultConfig(moduleId);
      }
      newAccess[moduleId][`${role}List`] = newValue
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      return newAccess;
    });
  };

  const handleSaveModuleAccess = async () => {
    setSavingModuleAccess(true);
    try {
      // Split on the way out: switches in one document, address lists in the other. Writing the
      // whole merged object to module_access would put the addresses back into the document every
      // member can read, which is the leak this split exists to close.
      const flags = {};
      const lists = {};
      Object.entries(moduleAccess).forEach(([id, cfg]) => {
        const f = {};
        const l = {};
        Object.entries(cfg || {}).forEach(([k, v]) => {
          if (k.endsWith("List")) l[k] = v;
          else f[k] = v;
        });
        flags[id] = f;
        if (Object.keys(l).length) lists[id] = l;
      });

      await setDoc(doc(db, "schools", activeSchoolId, "metadata", "module_access"), flags);
      await setDoc(
        doc(db, "schools", activeSchoolId, "metadata", "module_access_exceptions"), lists);
      alert(t('accessControl.accessSaved'));
    } catch (err) {
      console.error(err);
      alert(t('accessControl.accessFailed'));
    }
    setSavingModuleAccess(false);
  };

  const handleAddException = (moduleId, role) => {
    const inputVal = newExceptionInput[`${moduleId}_${role}`];
    if (!inputVal) return;

    setModuleAccess((prev) => {
      const newAccess = { ...prev };
      if (!newAccess[moduleId]) {
        newAccess[moduleId] = getDefaultConfig(moduleId);
      }
      const currentList = newAccess[moduleId][`${role}List`] || [];
      if (!currentList.includes(inputVal)) {
        newAccess[moduleId][`${role}List`] = [...currentList, inputVal];
      }
      return newAccess;
    });

    setNewExceptionInput((prev) => ({ ...prev, [`${moduleId}_${role}`]: "" }));
  };

  const handleRemoveException = (moduleId, role, exceptionValue) => {
    setModuleAccess((prev) => {
      const newAccess = { ...prev };
      if (!newAccess[moduleId]) return newAccess;

      const currentList = newAccess[moduleId][`${role}List`] || [];
      newAccess[moduleId][`${role}List`] = currentList.filter(
        (v) => v !== exceptionValue,
      );
      return newAccess;
    });
  };


  const handleSaveModuleAdmins = async () => {
    setSavingModuleAdmins(true);
    try {
      await setDoc(doc(db, "schools", activeSchoolId, "metadata", "module_admins"), moduleAdmins);
      alert(t('accessControl.adminsSaved'));
    } catch (err) {
      console.error(err);
      alert(t('accessControl.adminsFailed', { message: err.message }));
    }
    setSavingModuleAdmins(false);
  };

  const addModuleAdmin = (moduleId, email) => {
    if (!email) return;
    setModuleAdmins((prev) => ({
      ...prev,
      [moduleId]: [...new Set([...(prev[moduleId] || []), email])],
    }));
    setAdminPick((prev) => ({ ...prev, [moduleId]: "" }));
  };

  const removeModuleAdmin = (moduleId, email) => {
    setModuleAdmins((prev) => ({
      ...prev,
      [moduleId]: (prev[moduleId] || []).filter((e) => e !== email),
    }));
  };










  if (loading) return <div className="p-8">{t('accessControl.loading')}</div>;

  return (
    <div
      className="animate-fade-in new-theme"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="theme-tabs-bar" style={{ marginBottom: "1.5rem" }}>
        <div className="theme-tabs-group">
          <button
            className={`theme-tab ${topTab === "module_access" ? "active" : ""}`}
            onClick={() => setTopTab("module_access")}
          >
            <div className="theme-tab-icon"><Layers size={20} /></div>
            {t('accessControl.tabs.staff')}
          </button>
          <button
            className={`theme-tab ${topTab === "family_access" ? "active" : ""}`}
            onClick={() => setTopTab("family_access")}
          >
            <div className="theme-tab-icon"><Layers size={20} /></div>
            {t('accessControl.tabs.family')}
          </button>
          <button
            className={`theme-tab ${topTab === "by_person" ? "active" : ""}`}
            onClick={() => setTopTab("by_person")}
          >
            <div className="theme-tab-icon"><Users size={20} /></div>
            {t('accessControl.tabs.byPerson')}
          </button>
          <button
            className={`theme-tab ${topTab === "module_admins" ? "active" : ""}`}
            onClick={() => setTopTab("module_admins")}
          >
            <div className="theme-tab-icon"><Shield size={20} /></div>
            {t('accessControl.tabs.moduleAdmins')}
          </button>
        </div>

        <HeaderActionsPortal>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {(topTab === "module_access" || topTab === "family_access" || topTab === "by_person") && (
            <button className="btn-primary" onClick={handleSaveModuleAccess} disabled={savingModuleAccess} style={{ display: "flex", alignItems: "center", width: "auto", whiteSpace: "nowrap" }}>
              <Save size={18} style={{ marginRight: "0.5rem" }} />
              {savingModuleAccess ? "Saving..." : "Save"}
            </button>
          )}
          {topTab === "module_admins" && (
            <button className="btn-primary" onClick={handleSaveModuleAdmins} disabled={savingModuleAdmins} style={{ display: "flex", alignItems: "center", width: "auto", whiteSpace: "nowrap" }}>
              <Save size={18} style={{ marginRight: "0.5rem" }} />
              {savingModuleAdmins ? "Saving..." : "Save"}
            </button>
          )}
          
          
        </div>
        </HeaderActionsPortal>
      </div>

      

      {topTab === "module_access" && (
        <div
          style={{
            backgroundColor: "var(--color-bg-card)",
            borderRadius: isMobile ? 0 : "var(--radius-lg)",
            boxShadow: isMobile ? "none" : "var(--shadow-sm)",
            padding: isMobile ? "0.75rem" : "1.5rem",
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {renderSection(
            "Staff modules",
            "What teaching and non-teaching staff can open.",
            ["teacher", "staff"],
            (m) => m.audience !== AUDIENCE.FAMILY,
          )}
        </div>
      )}

      {topTab === "family_access" && (
        <div
          style={{
            backgroundColor: "var(--color-bg-card)",
            borderRadius: isMobile ? 0 : "var(--radius-lg)",
            boxShadow: isMobile ? "none" : "var(--shadow-sm)",
            padding: isMobile ? "0.75rem" : "1.5rem",
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {renderSection(
            "Student and parent modules",
            "Families have their own modules. Staff modules are never offered to them, so nothing else appears here.",
            ["student", "parent"],
            (m) => m.audience === AUDIENCE.FAMILY,
          )}
        </div>
      )}

      {topTab === "by_person" && (
        <div
          style={{
            backgroundColor: "var(--color-bg-card)",
            borderRadius: isMobile ? 0 : "var(--radius-lg)",
            boxShadow: isMobile ? "none" : "var(--shadow-sm)",
            padding: isMobile ? "0.75rem" : "1.5rem",
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          <h3 style={{ margin: "0 0 0.3rem", fontSize: "1rem", fontWeight: "700" }}>{t('accessControl.byPersonTitle')}</h3>
          <p style={{ margin: "0 0 1rem", fontSize: "0.83rem", color: "var(--color-text-muted)" }}>
            {t('accessControl.byPersonIntro')}
          </p>
          {renderByPerson()}
        </div>
      )}

      {topTab === "module_admins" && (
        <div
          style={{
            backgroundColor: "var(--color-bg-card)",
            borderRadius: isMobile ? 0 : "var(--radius-lg)",
            boxShadow: isMobile ? "none" : "var(--shadow-sm)",
            padding: isMobile ? "0.75rem" : "1.5rem",
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          <h3 style={{ fontSize: "1rem", fontWeight: "700", margin: "0 0 0.25rem" }}>
            {t('accessControl.moduleAdminsTitle')}
          </h3>
          <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0 0 0.25rem", maxWidth: "72ch", lineHeight: 1.6 }}>
            {t('accessControl.moduleAdminsIntro')}
          </p>
          <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0 0 1rem", maxWidth: "72ch", lineHeight: 1.6 }}>
            {t('accessControl.moduleAdminsWhoCanEdit')}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {AVAILABLE_MODULES
              .filter((m) => MODULE_ADMIN_CAPABLE.has(m.id))
              .map((mod) => {
                const list = moduleAdmins[mod.id] || [];
                return (
                  <div
                    key={mod.id}
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-md)",
                      padding: "0.75rem 1rem",
                      display: "flex",
                      gap: "1rem",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ minWidth: "180px", fontWeight: "600", fontSize: "0.9rem", paddingTop: "0.4rem" }}>
                      {mod.name}
                    </div>

                    <div style={{ flex: 1, minWidth: "260px", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <select
                          className="input-field"
                          style={{ flex: 1, margin: 0 }}
                          value={adminPick[mod.id] || ""}
                          onChange={(e) => setAdminPick((prev) => ({ ...prev, [mod.id]: e.target.value }))}
                        >
                          <option value="">-- Add a member of staff --</option>
                          {staffList
                            .filter((st) => !list.includes(st.id))
                            .map((st) => (
                              <option key={st.id} value={st.id}>
                                {st.initial ? `${st.initial} — ${st.id}` : st.id}
                              </option>
                            ))}
                        </select>
                        <button
                          className="btn-outline"
                          onClick={() => addModuleAdmin(mod.id, adminPick[mod.id])}
                          disabled={!adminPick[mod.id]}
                          style={{ width: "auto", padding: "0 1rem" }}
                        >
                          Add
                        </button>
                      </div>

                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                        {list.length === 0 ? (
                          <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                            {t('accessControl.noModuleAdmin')}
                          </span>
                        ) : list.map((email) => (
                          <span
                            key={email}
                            style={{
                              backgroundColor: "var(--color-bg-app)",
                              border: "1px solid var(--color-border)",
                              borderRadius: "1rem",
                              padding: "0.2rem 0.7rem",
                              fontSize: "0.82rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.45rem",
                            }}
                          >
                            {exceptionLabel(email)}
                            <button
                              onClick={() => removeModuleAdmin(mod.id, email)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-danger)", display: "flex", padding: 0 }}
                              aria-label={t('accessControl.removePerson', { email })}
                            >
                              <Trash2 size={13} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      

      {/* ADD/EDIT MODAL */}
      

      {/* IMPORT MODAL */}
      
    </div>
  );
};

export default AccessControl;
